/**
 * Button Component
 * Flexible, accessible button component with multiple variants
 */

import React from 'react';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'ghost' | 'outline';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';
export type ButtonType = 'button' | 'submit' | 'reset';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Button type attribute */
  type?: ButtonType;
  /** Whether button is in loading state */
  isLoading?: boolean;
  /** Whether button is disabled */
  isDisabled?: boolean;
  /** Whether button spans full width */
  isFullWidth?: boolean;
  /** Left icon/element */
  iconLeft?: React.ReactNode;
  /** Right icon/element */
  iconRight?: React.ReactNode;
  /** Button text/children */
  children: React.ReactNode;
}

/**
 * Button Component
 *
 * @example
 * // Primary button
 * <Button variant="primary">Click me</Button>
 *
 * @example
 * // Loading state
 * <Button isLoading>Submitting...</Button>
 *
 * @example
 * // With icon
 * <Button iconLeft={<CheckIcon />}>Save</Button>
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      type = 'button',
      isLoading = false,
      isDisabled = false,
      isFullWidth = false,
      iconLeft,
      iconRight,
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    // Combine base and variant classes
    const classNames = `${styles.button} ${styles[`variant-${variant}`]} ${styles[`size-${size}`]} ${
      isFullWidth ? styles.fullWidth : ''
    } ${isLoading ? styles.loading : ''} ${className}`;

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled || isLoading}
        className={classNames}
        {...props}
      >
        {isLoading ? (
          <>
            <span className={styles.spinner}>⏳</span>
            {children}
          </>
        ) : (
          <>
            {iconLeft && <span className={styles.iconLeft}>{iconLeft}</span>}
            <span>{children}</span>
            {iconRight && <span className={styles.iconRight}>{iconRight}</span>}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
